import React, { useContext, useMemo } from 'react';
import { ThemeContext } from 'styled-components';
import { useRecoilValue } from 'recoil';
import { capitalize, compact } from 'lodash';
import { useQuery } from '@tanstack/react-query';
import {
  type FrequencyEnum,
  investmentLayeredProxyMetrics,
  type LayeredProxyMetrics,
  type ProxyTypeEnum,
} from 'venn-api';
import { proxySetIdState } from 'venn-state';
import { LoadingSize, Spinner } from 'venn-ui-kit';
import { ThreeDiamondsIcon } from '../../common/ThreeDiamondsIcon';
import {
  createProxyMetricsKey,
  type DataPoint,
  FREQUENCY_TO_ENUM,
  getFormattedProxyType,
  hasInterpolateProxyType,
  isRequestSuccessful,
  Numbers,
  ReturnsCalculations,
} from 'venn-utils';
import { Legend, type LegendSeries } from '../../legend/Legend';
import InterpolationSnapshot from './InterpolationSnapshot';
import ProxyInfoChart from './ProxyInfoChart';
import { extractProxiedReturns, formatDateRange } from '../../utils/proxyUtils';
import { getProxyRange, type ProxyMetric } from './utils';

type ProxyInfoProps = {
  investment: {
    id: string;
    updated: number;
    frequency: number | undefined;
    startRange: number | undefined;
    unproxiedEndRange: number | undefined;
    unproxiedStartRange: number | undefined;
    endRange: number | undefined;
  };
};

export type ProxySchemes = {
  proxyLine: string;
  invertedSubjectLine: string;
  extrapolation: string;
};

export type LayeredProxyReturns = {
  proxyReturns: DataPoint[];
  extrapolatedReturns: DataPoint[];
  unproxiedInvestmentReturns: DataPoint[];
};

export const formatLayeredProxyReturns = (
  layeredProxyMetrics: LayeredProxyMetrics | null | undefined,
): LayeredProxyReturns => {
  if (!layeredProxyMetrics) {
    return {
      proxyReturns: [],
      extrapolatedReturns: [],
      unproxiedInvestmentReturns: [],
    };
  }

  const { cumulativeProxiedReturns, proxyMetricsList, extrapolatedStart, investmentReturns } = layeredProxyMetrics;

  const allTogether = cumulativeProxiedReturns ? ReturnsCalculations.toDataPoints(cumulativeProxiedReturns) : [];
  const filteredProxyMetricsList = proxyMetricsList.filter(({ proxyType }) => proxyType !== 'EXTRAPOLATE');

  const { proxy, extrapolated } = extractProxiedReturns(
    allTogether,
    filteredProxyMetricsList?.at(0)?.proxiedStart,
    filteredProxyMetricsList?.at(-1)?.proxiedEnd,
    extrapolatedStart,
  );

  const unproxiedInvestmentReturns = ReturnsCalculations.toDataPoints(investmentReturns ?? []);

  return {
    proxyReturns: proxy,
    extrapolatedReturns: extrapolated,
    unproxiedInvestmentReturns,
  };
};

export const createLegendSeries = (
  proxyReturns: DataPoint[],
  unproxiedInvestmentReturns: DataPoint[],
  extrapolatedReturns: DataPoint[],
  schemes: ProxySchemes,
  firstProxyType: ProxyTypeEnum | undefined,
  secondProxyType: ProxyTypeEnum | undefined,
): LegendSeries[] => {
  return compact([
    proxyReturns.length > 0 ? { color: schemes.proxyLine, name: 'Proxy' } : null,
    unproxiedInvestmentReturns.length > 0
      ? hasInterpolateProxyType([firstProxyType, secondProxyType])
        ? {
            color: schemes.invertedSubjectLine,
            name: 'Investment',
            customIcon: (
              <ThreeDiamondsIcon color={schemes.invertedSubjectLine} className="inline-flex items-center mr-[10px]" />
            ),
          }
        : { color: schemes.invertedSubjectLine, name: 'Investment' }
      : null,
    extrapolatedReturns.length > 0 ? { color: schemes.extrapolation, name: 'Extrapolation' } : null,
  ]);
};

export const getProxyEndDate = (layeredProxyMetrics: LayeredProxyMetrics | null | undefined): number | undefined => {
  return layeredProxyMetrics?.proxyMetricsList.at(-1)?.proxiedEnd;
};

export const useProxyMetricsData = (
  investmentId: string,
  investmentLastUpdated: number,
  proxySetId?: string | null,
) => {
  const {
    data: layeredProxyMetrics,
    status,
    error: queryError,
  } = useQuery<LayeredProxyMetrics | null, Error>(
    [...createProxyMetricsKey(investmentId), investmentLastUpdated, proxySetId],
    async () => {
      try {
        const result = await investmentLayeredProxyMetrics(investmentId, proxySetId ?? '');

        if (isRequestSuccessful(result)) {
          return result.content;
        }

        throw new Error('Proxy fetch failed');
      } catch (e: unknown) {
        const error = e as { name?: string; content?: { message?: string } };

        if (error?.name !== 'AbortError') {
          throw new Error(error?.content?.message || 'Proxy fetch failed');
        }
        throw e;
      }
    },
  );

  const layeredProxyReturns = useMemo(() => formatLayeredProxyReturns(layeredProxyMetrics), [layeredProxyMetrics]);

  return {
    layeredProxyMetrics,
    status,
    error: queryError?.message || null,
    ...layeredProxyReturns,
  };
};

export const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex flex-col mb-[3px]">
    <div className="text-[10px] font-bold">{label}</div>
    <div className="p-[3px_0_4px] text-[14px] text-venn-pale-gold">{value}</div>
  </div>
);

export const ProxyRangeSection: React.FC<{
  proxyType: ProxyTypeEnum;
  frequency: FrequencyEnum;
  startDate?: number;
  endDate?: number;
  numLags?: number;
  unproxiedFrequency?: string;
}> = ({ proxyType, startDate, endDate, frequency, numLags, unproxiedFrequency }) => (
  <>
    <InfoRow label="Proxy Type" value={getFormattedProxyType(proxyType)} />
    <div className="flex mb-[3px]">
      {startDate && endDate && (
        <div className="flex flex-col grow">
          <div className="text-[10px] font-bold">Proxy Range</div>
          <div className="p-[3px_0_4px] text-[14px] text-venn-pale-gold">
            {formatDateRange(startDate, endDate, frequency)}
          </div>
        </div>
      )}
      {numLags ? (
        <div className="flex flex-col">
          <div className="text-[10px] font-bold">
            {unproxiedFrequency ? `${capitalize(unproxiedFrequency)} ` : ''}Lag
          </div>
          <div className="p-[3px_0_4px] text-[14px] text-right">{numLags}</div>
        </div>
      ) : null}
    </div>
  </>
);

export const ProxyMetricItem: React.FC<{
  metric: ProxyMetric;
  investment: {
    id: string;
    updated: number;
    frequency: number | undefined;
    startRange: number | undefined;
    unproxiedEndRange: number | undefined;
    unproxiedStartRange: number | undefined;
    endRange: number | undefined;
  };
}> = ({ metric, investment }) => {
  const investmentFrequency = investment.frequency;
  const { proxyType, extrapolated, numLags, extrapolatedStart, extrapolatedEnd } = metric;
  const proxyRange = getProxyRange({ metric, investment });
  const { name, frequency, unproxiedFrequency } = metric.proxy;

  const displayFrequency = (frequency ?? investmentFrequency) as keyof typeof FREQUENCY_TO_ENUM;
  const frequencyEnum = FREQUENCY_TO_ENUM[displayFrequency];
  return (
    <>
      {name && <InfoRow label="Returns Proxy" value={name} />}

      {proxyType && (
        <ProxyRangeSection
          proxyType={proxyType}
          startDate={proxyRange?.start}
          endDate={proxyRange?.end}
          frequency={frequencyEnum}
          numLags={numLags}
          unproxiedFrequency={unproxiedFrequency}
        />
      )}

      {extrapolated && proxyType !== 'EXTRAPOLATE' && (
        <ProxyRangeSection
          proxyType="EXTRAPOLATE"
          startDate={extrapolatedStart}
          endDate={extrapolatedEnd}
          frequency={frequencyEnum}
        />
      )}

      <hr className="h-px border-0 bg-venn-grey-60" />
    </>
  );
};

export type LayeredProxyInfoContentProps = {
  layeredProxyMetrics: LayeredProxyMetrics;
  proxyReturns: DataPoint[];
  extrapolatedReturns: DataPoint[];
  unproxiedInvestmentReturns: DataPoint[];
  schemes: ProxySchemes;
  investment: {
    id: string;
    updated: number;
    frequency: number | undefined;
    startRange: number | undefined;
    unproxiedEndRange: number | undefined;
    unproxiedStartRange: number | undefined;
    endRange: number | undefined;
  };
};

export const LayeredProxyInfoContent: React.FC<LayeredProxyInfoContentProps> = ({
  layeredProxyMetrics,
  proxyReturns,
  extrapolatedReturns,
  unproxiedInvestmentReturns,
  schemes,
  investment,
}) => {
  const { correlation, proxyMetricsList } = layeredProxyMetrics;
  const proxiesShortInfo = proxyMetricsList.map(({ proxyType, proxiedStart, proxiedEnd }) => ({
    proxyType,
    proxiedStart,
    proxiedEnd,
  }));
  const firstProxyType = proxyMetricsList[0]?.proxyType;
  const secondProxyType = proxyMetricsList[1]?.proxyType;

  const legend = useMemo(
    () =>
      createLegendSeries(
        proxyReturns,
        unproxiedInvestmentReturns,
        extrapolatedReturns,
        schemes,
        firstProxyType,
        secondProxyType,
      ),
    [proxyReturns, unproxiedInvestmentReturns, extrapolatedReturns, schemes, firstProxyType, secondProxyType],
  );

  return (
    <div className="w-[300px] p-[5px_5px_0] text-venn-white">
      {proxyMetricsList.map((proxyMetric) => (
        <ProxyMetricItem key={proxyMetric.proxy.id} metric={proxyMetric} investment={investment} />
      ))}

      <div className="flex justify-between items-center">
        <div className="text-[10px] font-bold">Correlation</div>
        <div className="p-[3px_0_4px] text-[14px]">{Numbers.formatCorrelation(correlation)}</div>
      </div>

      <div className="mt-[14px]">
        {firstProxyType === 'INTERPOLATE' ? (
          <InterpolationSnapshot
            primarySeries={unproxiedInvestmentReturns}
            overlaySeries={proxyReturns}
            extrapolateSeries={extrapolatedReturns}
            height={54}
            invert
          />
        ) : (
          <ProxyInfoChart
            className="w-full"
            investment={unproxiedInvestmentReturns}
            proxy={proxyReturns}
            extrapolation={extrapolatedReturns}
            proxiesShortInfo={proxiesShortInfo}
          />
        )}
      </div>
      <div className="flex justify-evenly border-solid border-0 border-t border-venn-grey-90 pt-[5px] mt-[10px] text-base font-normal text-venn-grey">
        <Legend className="proxy-info-modal-legend flex-nowrap" series={legend} />
      </div>
    </div>
  );
};

const LayeredProxyInfo: React.FC<React.PropsWithChildren<ProxyInfoProps>> = ({ investment }) => {
  const investmentId = investment.id;
  const investmentLastUpdated = investment.updated;
  const proxySetId = useRecoilValue(proxySetIdState);
  const { Schemes } = useContext(ThemeContext);

  const { layeredProxyMetrics, status, error, proxyReturns, extrapolatedReturns, unproxiedInvestmentReturns } =
    useProxyMetricsData(investmentId, investmentLastUpdated, proxySetId);

  if (error) {
    return <p>{error}</p>;
  }

  if (status === 'loading' || !layeredProxyMetrics) {
    return <Spinner size={LoadingSize.medium} />;
  }

  return (
    <LayeredProxyInfoContent
      layeredProxyMetrics={layeredProxyMetrics}
      proxyReturns={proxyReturns}
      extrapolatedReturns={extrapolatedReturns}
      unproxiedInvestmentReturns={unproxiedInvestmentReturns}
      schemes={Schemes.Proxy}
      investment={investment}
    />
  );
};

export default LayeredProxyInfo;
